import { AfterViewInit, Component, inject, model, OnInit, signal, viewChild } from '@angular/core';
import { TestUser, TestUserInput, UserReference } from '../../../api/types';
import {
  SynAlertComponent,
  SynButtonComponent,
  SynIconComponent,
  SynInputComponent,
  SynTabComponent,
  SynTabGroupComponent, SynTabPanelComponent,
  SynValidateComponent,
} from '@synergy-design-system/angular';
import { ActivatedRoute, Router } from '@angular/router';
import { AddAttributeDialogComponent } from './add-attribute-dialog.component';
import { KeyValue } from '../../types';
import { SynTabShowEvent } from '@synergy-design-system/components';
import { TestUserFormComponent } from './test-user-form.component';
import { getUserData } from '../../../api/auth';
import { ColumnDescription, TableComponent } from '../../components/table';
import { ServicePortalApi } from '../../../api/api';
import { handleError, setDuplicateUsernameError } from '../../../errors';
import { UserSearchComponent } from '../../components/user-search.component';
import { SavedToastComponent } from '../../components/saved-toast.component';

type TestUserTab = 'properties' | 'attributes' | 'permissions';

function isTestUserTab(value: string | undefined): value is TestUserTab {
  switch (value) {
    case 'properties':
    case 'attributes':
    case 'permissions':
      return true;

    default:
      return false;
  }
}

@Component({
  selector: 'app-test-user-settings',
  template: `
    <syn-tab-group (synTabShowEvent)="onShowTab($event)" #tabGroup>
      <syn-tab [active]="activeTab() === 'properties'"
               slot="nav" panel="properties" i18n="@@properties"
               data-e2e="tab-properties">Properties
      </syn-tab>
      <syn-tab [active]="activeTab() === 'attributes'"
               slot="nav" panel="attributes"
               data-e2e="tab-attributes"
               i18n="@@attributes">Attributes
      </syn-tab>
      <syn-tab [active]="activeTab() === 'permissions'"
               slot="nav" panel="permissions"
                data-e2e="tab-permissions">
        <span i18n="@@permissions">Permissions</span>
      </syn-tab>

      <syn-tab-panel name="properties">
        <app-test-user-form
          [user]="user()"
          submitText="Save Changes"
          i18n-submitText="@@save-changes"
          submitIcon="save"
          (submitUser)="onSubmit($event)"
        />
      </syn-tab-panel>

      <syn-tab-panel name="attributes">
        <div class="flex-col gap-1 form-width" style="padding-right: 5px;">
          @for (attribute of sortedAttributes(); track $index) {
            <div class="flex-row gap-1">
              <syn-validate on="live" class="flex-grow-1">
                <syn-input [(value)]="attribute.value" required [maxlength]="200"
                           [name]="'attribute_' + attribute.key"
                           [label]="attribute.key"
                />
              </syn-validate>
              <syn-button style="margin-top: auto" (click)="removeAttribute(attribute)"
                          [disabled]="loading()"
                          title="Remove Attribute" i18n-title="@@remove-attribute">
                <syn-icon name="delete_fill" slot="prefix" />
              </syn-button>
            </div>
          }
        </div>
        <syn-button
          class="margin-top-1"
          (click)="showAddAttributeDialog()"
          [disabled]="loading()"
          style="padding-left: 5px;"
          data-e2e="btn-add-attribute"
        >
          <syn-icon name="add_fill" slot="prefix" />
          <span i18n="@@add-attribute">Add Attribute</span>
        </syn-button>
      </syn-tab-panel>

      <syn-tab-panel name="permissions">
        <div style="padding-right: 5px;">
          <syn-alert variant="primary" [open]="true" class="margin-bottom-1">
            <syn-icon name="info_fill" slot="icon" />
            <span i18n="@@owners-help">Owners can edit and delete test users.</span>
          </syn-alert>

          <div class="margin-bottom-1" style="padding-left: 5px;">
            <app-user-search
              [disabled]="loading()"
              actionText="Add Owner"
              i18n-actionText="@@add-owner"
              (userSelected)="onAddOwner($event)"
              #userSearch
            />
          </div>

          <ng-template #rowTemplate let-value>
            <!-- Enable type checking in the template -->
            @let user = identity(value);
            <tr>
              <td>{{ user.username }}</td>
              <td>{{ user.id }}</td>
              <td>
                <syn-button
                  [disabled]="isCurrentUser(user.id) || loading()"
                  (click)="onRemoveOwner(user)"
                >
                  <syn-icon name="delete_fill" slot="prefix" />
                  <span i18n="@@remove-owner">Remove Owner</span>
                </syn-button>
              </td>
            </tr>
          </ng-template>
          <app-table
            [values]="user().owners"
            [columns]="columns"
            [rowTemplate]="rowTemplate"
            class="margin-top-1"
          />
        </div>
      </syn-tab-panel>
    </syn-tab-group>

    <app-saved-toast #savedToast />

    <app-add-attribute-dialog (add)="onAddAttribute($event)" #addAttributeDialog />
  `,
  styles: [],
  imports: [
    SynButtonComponent,
    SynIconComponent,
    SynInputComponent,
    SynValidateComponent,
    AddAttributeDialogComponent,
    SynTabGroupComponent,
    SynTabComponent,
    SynTabPanelComponent,
    TestUserFormComponent,
    SynAlertComponent,
    TableComponent,
    UserSearchComponent,
    SavedToastComponent,
  ],
})
export class TestUserSettingsComponent implements OnInit, AfterViewInit {
  activeRoute = inject(ActivatedRoute);
  router = inject(Router);
  api = inject(ServicePortalApi);

  user = model.required<TestUser>();
  sortedAttributes = signal<KeyValue[]>([]);
  activeTab = signal<TestUserTab>('properties');
  loading = signal(false);

  addAttributeDialog = viewChild.required<AddAttributeDialogComponent>('addAttributeDialog');
  addOwnerInput = viewChild.required<UserSearchComponent>('userSearch');
  savedToast = viewChild.required<SavedToastComponent>('savedToast');

  columns: ColumnDescription<UserReference>[] = [
    {
      label: $localize`:@@username:Username`,
      getField: (user) => user.username ?? '',
      style: 'width: 40%',
    },
    {
      label: $localize`:@@uuid:UUID`,
      getField: (user) => user.id ?? '',
      style: 'width: 40%',
    },
    {
      label: $localize`:@@action:Action`,
      getField: () => '',
      style: 'width: 20%',
    },
  ];

  ngOnInit(): void {
    this.computeSortedAttributes();
    this.activeRoute.params.subscribe((params) => {
      const tab = params['tab'] ?? 'properties';
      if (isTestUserTab(tab)) {
        this.activeTab.set(tab);
      }
    });
  }

  ngAfterViewInit() {
    const addOwner = this.activeRoute.snapshot.queryParams['addOwner'];
    if (typeof addOwner === 'string') {
      this.addOwnerInput().setValue(addOwner);
    }
  }

  onShowTab(event: SynTabShowEvent) {
    const synEvent = event as SynTabShowEvent;
    const tab = synEvent.detail.name;
    if (isTestUserTab(tab)) {
      void this.router.navigate(['test-users', this.user().id, tab]);

      this.activeTab.set(tab);
    }
  }

  computeSortedAttributes() {
    const attributeList: { key: string, value: string }[] = [];

    const attributes = this.user().attributes;
    for (const key in attributes) {
      const values = attributes[key];
      for (const value of values) {
        attributeList.push({ key, value });
      }
    }
    attributeList.sort((a, b) => a.key.localeCompare(b.key));

    this.sortedAttributes.set(attributeList);
  }

  getAttributeMap(): Record<string, string[]> {
    const map: Record<string, string[]> = {};
    for (const attribute of this.sortedAttributes()) {
      const values = map[attribute.key];
      if (values) {
        values.push(attribute.value);
      } else {
        map[attribute.key] = [attribute.value];
      }
    }
    return map;
  }

  removeAttribute(attribute: KeyValue) {
    const attribtues = this.sortedAttributes();
    const newAttributes = attribtues.filter((a) => a !== attribute);
    this.sortedAttributes.set(newAttributes);

    this.onSubmit(this.user());
  }

  onSubmit(user: TestUserInput) {
    user.attributes = this.getAttributeMap();
    this.loading.set(true);
    this.api.updateTestUser(this.user().id, user, (result) => {
      this.user.set(result);
      this.computeSortedAttributes();
      this.loading.set(false);
      this.addOwnerInput().clear();
      this.savedToast().toast();
    }, (error) => {
      this.loading.set(false);
      if (error.status === 400) {
        if (error.body.error === 'Owner does not exist') {
          this.addOwnerInput().setUserDoesNotExistError();
          return;
        }
      } else if (error.status === 409) {
        setDuplicateUsernameError();
        return;
      }
      handleError(error);
    });
  }

  showAddAttributeDialog() {
    this.addAttributeDialog().show();
  }

  onAddAttribute(attribute: KeyValue) {
    this.sortedAttributes.update((old) => {
      return [...old, attribute];
    });

    this.onSubmit(this.user());
  }

  isCurrentUser(id: string | undefined) {
    const currentUser = getUserData();
    return currentUser.id === id;
  }

  onRemoveOwner(ownerToRemove: UserReference) {
    const user = { ...this.user() };
    user.owners = user.owners.filter((u) => u !== ownerToRemove);

    this.onSubmit(user);
  }

  onAddOwner(newOwner: UserReference) {
    const user = { ...this.user() };
    user.owners = [...this.user().owners, newOwner];
    this.onSubmit(user);
  }

  identity(value: UserReference): UserReference {
    return value;
  }
}
